import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { ScheduleTypes } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { ScopedSingleLocationFilterMenu } from '@frontend/location-filter-menu';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { TableColumnConfig, Table, Text, Chip, Alert, useFormField } from '@frontend/design-system';
import {
  ExtendedScheduleRequestHistoryItem,
  useGetScheduleRequestHistoryData,
} from '../../hooks/scheduler-v3/use-get-schedule-request-history-data';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { AppointmentTypeChip } from '../appointment-type-chip';
import { StatusBadge } from '../status-badge';
import { TableHeaderWithTimezoneInfo } from '../v3/table-header-with-timezone-info';
import { CustomRowsPerPage } from './CustomRowsPerPage';
import { CustomTablePagination } from './CustomTablePagination';
import { getAppointmentOutcomeColor, getAppointmentOutcomeText } from './helpers';

const DEFAULT_PAGE_SIZE = 25;

export const ScheduleRequestsHistoryTable = () => {
  const { t } = useTranslation('schedule-request-history');
  const { selectedLocationIds } = useAppScopeStore();
  const { customizationFlags } = useAppFlagStore();
  const { locationId: queryParamLocationId } = useSearch({ from: '/schedule/request-history' });
  const { getCFLocationMap } = useSchedulingLocationInfo();
  const { isSchedulerV2FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isScheduleDefinitionsEnabled } = SchedulerV3.Helpers.useGetScheduleDefinitionsFeatureFlagDetails();
  const { isPreferenceBasedRequestEnabled } = SchedulerV3.Helpers.useGetPreferenceBasedRequestFeatureFlagDetails();
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();

  const { isOnlineSchedulingEnabledForAnyLocation, onlineSchedulingCFEnabledLocationIds } = useMemo(() => {
    const onlineSchedulingCFLocationMap = getCFLocationMap('onlinescheduling');
    const isOnlineSchedulingEnabledForAnyLocation = Object.values(onlineSchedulingCFLocationMap).some((value) => value);
    const onlineSchedulingCFEnabledLocationIds =
      Object.keys(onlineSchedulingCFLocationMap).filter((locationId) => !!onlineSchedulingCFLocationMap[locationId]) ||
      [];
    return {
      isOnlineSchedulingEnabledForAnyLocation,
      onlineSchedulingCFEnabledLocationIds,
    };
    // NOTE: this is workaround for the issue where the customization flags are not immediately updated in the store when locations are changed
  }, [selectedLocationIds, customizationFlags]);

  const selectedLocationField = useFormField(
    {
      type: 'checklist',
      value:
        queryParamLocationId && onlineSchedulingCFEnabledLocationIds.includes(queryParamLocationId)
          ? [queryParamLocationId]
          : [onlineSchedulingCFEnabledLocationIds[0]],
    },
    [onlineSchedulingCFEnabledLocationIds, queryParamLocationId]
  );

  const { formatDateAndTimeByLocation } = useLocationTimezone();
  const formatDateAndTime = formatDateAndTimeByLocation(selectedLocationField.value[0]);

  const [pageConfig, setPageConfig] = useState<ScheduleTypes.ScheduleRequestHistoryPageConfig>({
    offset: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const {
    data: scheduleRequestsHistoryList,
    isLoading: isLoadingScheduleRequestsHistoryList,
    refetch: refetchScheduleRequestsHistory,
  } = useGetScheduleRequestHistoryData(selectedLocationField.value[0], pageConfig);

  const columnConfig: TableColumnConfig<ExtendedScheduleRequestHistoryItem>[] = useMemo(
    () => [
      {
        Header: t('Requested Date'),
        id: 'createdAt',
        disableSortBy: true,
        width: 220,
        omit: isSchedulerV2FlagEnabled,
        accessor: (row) => row.createdAt,
        cellRenderer: (row) => {
          return <Text size='medium'>{formatDate(row, 'MMMM D, YYYY h:mm A')}</Text>;
        },
      },
      {
        Header: t('Recipient'),
        id: 'name',
        width: 180,
        accessor: (row) => row.recipient,
        cellRenderer: (row) => {
          return (
            <Text weight='bold' size='medium'>
              {row}
            </Text>
          );
        },
      },
      {
        Header: t('Origin'),
        id: 'origin',
        disableSortBy: true,
        omit: !isPreferenceBasedRequestEnabled,
        accessor: (row) => row?.isAiRequest,
        width: 150,
        cellRenderer: (row) =>
          row ? <StatusBadge variant='ai-requested' /> : <StatusBadge variant='online-requested' />,
      },
      {
        Header: (
          <TableHeaderWithTimezoneInfo
            title={t('Appointment Date & Time')}
            locationId={selectedLocationField.value[0]}
          />
        ),
        id: 'message-date-time',
        disableSortBy: true,
        accessor: (row) => row?.messageDatetime,
        width: 230,
        cellRenderer: (row) => {
          if (!row) return <Text size='medium'>-</Text>;
          return <Text size='medium'>{formatDateAndTime(row, 'MMMM D, YYYY h:mm A')}</Text>;
        },
      },
      {
        Header: t('Reviewed By'),
        id: 'reviewed-by',
        disableSortBy: true,
        width: 150,
        accessor: (row) => row?.reviewedBy,
        cellRenderer: (row) => {
          if (!row) return <Text size='medium'>-</Text>;
          return <Text size='medium'>{row}</Text>;
        },
      },
      {
        Header: t('Review Date'),
        id: 'review-date',
        disableSortBy: true,
        width: 216,
        accessor: (row) => row?.reviewedAt,
        cellRenderer: (row) => {
          if (!row) return <Text size='medium'>-</Text>;
          return <Text size='medium'>{dayjs(row).format('MMMM D, YYYY h:mm A')}</Text>;
        },
      },
      {
        Header: t('Appointment Type'),
        id: 'appointment-type',
        disableSortBy: true,
        width: 160,
        accessor: (row) => row,
        cellRenderer: (row) => {
          if (!row) return <Text size='medium'>-</Text>;
          return <AppointmentTypeChip maxWidth={154} name={row?.appointmentType} color={row?.appointmentTypeColor} />;
        },
      },
      {
        Header: t('Service Type'),
        id: 'bookable-appointment-type',
        disableSortBy: true,
        width: 160,
        omit: !isScheduleDefinitionsEnabled || !isIntegratedOffice,
        accessor: (row) => row,
        cellRenderer: (row) => {
          if (!row?.bookableAppointmentType) return <Text size='medium'>-</Text>;
          return <Text size='medium'>{row?.bookableAppointmentType}</Text>;
        },
      },
      {
        Header: t('Appointment Outcome'),
        id: 'appointment-outcome',
        disableSortBy: true,
        accessor: (row) => row?.appoinmentOutcome,
        cellRenderer: (row) => {
          if (!row) return <Text size='medium'>-</Text>;
          return (
            <Chip variant={getAppointmentOutcomeColor(row)} maxWidth={'150px'}>
              {getAppointmentOutcomeText(row)}
            </Chip>
          );
        },
      },
    ],
    [
      formatDateAndTime,
      isIntegratedOffice,
      isPreferenceBasedRequestEnabled,
      isScheduleDefinitionsEnabled,
      isSchedulerV2FlagEnabled,
      selectedLocationField.value,
      t,
    ]
  );

  const hasOneLocationAvailable = selectedLocationIds.length === 1;

  if (!isOnlineSchedulingEnabledForAnyLocation) {
    return (
      <Alert type='warning'>
        {t(
          "It looks like you don't have permission to view this page. Please contact your system administrator for more information."
        )}
      </Alert>
    );
  }

  return (
    <>
      <Table
        colConfig={columnConfig}
        data={scheduleRequestsHistoryList}
        isLoading={isLoadingScheduleRequestsHistoryList}
        hasGlobalSearch
        fullHeight
        fullHeightConfig={{
          minHeight: 230,
          // NOTE - The offset is to account for the height of the custom pagination container
          offset: 40,
        }}
        tableActions={[
          {
            Icon: () => <Icon name='update' size={16} />,
            label: t('Refresh Data'),
            onClick: () => refetchScheduleRequestsHistory(),
            trackingId: 'refresh-data',
            type: 'icon',
          },
        ]}
        customToolbarRender={() => {
          return (
            !hasOneLocationAvailable && (
              <div css={{ marginRight: 'auto' }}>
                <ScopedSingleLocationFilterMenu
                  field={selectedLocationField}
                  locationIds={onlineSchedulingCFEnabledLocationIds}
                  css={locationDropdownStyle}
                />
              </div>
            )
          );
        }}
      />
      <div css={customPaginationContainerStyles}>
        <CustomRowsPerPage
          rowsPerPageOptions={[DEFAULT_PAGE_SIZE, 50]}
          handleRowsPerPageChange={(pageSize) => {
            setPageConfig((prev) => ({ ...prev, offset: 0, pageSize: pageSize }));
          }}
        />
        <CustomTablePagination
          page={Math.floor(pageConfig.offset / pageConfig.pageSize) + 1}
          hasNextPage={scheduleRequestsHistoryList.length === pageConfig.pageSize}
          hasPrevPage={pageConfig.offset > 0}
          handlePageChange={(pageNumber: number) => {
            setPageConfig((prev) => ({
              ...prev,
              offset: (pageNumber - 1) * pageConfig.pageSize,
              pageSize: pageConfig.pageSize,
            }));
          }}
        />
      </div>
    </>
  );
};

const customPaginationContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
});

const locationDropdownStyle = css({
  background: theme.colors.primary50,
  color: theme.colors.white,
  p: { color: theme.colors.white },
});
